import { useEffect, useRef, useState, type ReactNode } from 'react';

interface DetailSectionProps {
  title: string;
  icon?: string;
  /** This section's key in the page's section tree. */
  sectionKey: string;
  /** The page's currently-selected section key. */
  activeKey: string;
  /** Called when the user opens this section (keeps tree/URL in sync). */
  onActivate: (key: string) => void;
  headerActions?: ReactNode;
  children: ReactNode;
}

// Detail-page section container (jobs / clients / employees …).
//  • Desktop (md+): the left-rail tree picks ONE section; that section shows as
//    a plain card — no collapse chrome, the tree already does the navigation.
//  • Mobile: every section stacks as a collapsible container. The active one is
//    open; tapping a header opens that section (accordion) and syncs the page's
//    active key, so the tree / Sections sheet stay consistent.
// Content only mounts while open, so inactive sections don't fetch or render.
export const DetailSection = ({
  title,
  icon,
  sectionKey,
  activeKey,
  onActivate,
  headerActions,
  children,
}: DetailSectionProps) => {
  const isActive = activeKey === sectionKey;
  const ref = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
  );
  const [mobileOpen, setMobileOpen] = useState(isActive);
  const prevActive = useRef(isActive);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const fn = () => setIsMobile(mq.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  // Selection from the tree / Sections sheet opens this section and closes the
  // rest (accordion); on a phone, scroll the opened section into view.
  useEffect(() => {
    if (isActive && !prevActive.current) {
      setMobileOpen(true);
      if (window.matchMedia('(max-width: 767px)').matches) {
        setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
    if (!isActive) setMobileOpen(false);
    prevActive.current = isActive;
  }, [isActive]);

  const open = isMobile ? mobileOpen : true;
  const visible = isMobile ? true : isActive;

  const toggle = () => {
    if (!isMobile) return;
    if (mobileOpen) setMobileOpen(false);
    else { setMobileOpen(true); onActivate(sectionKey); }
  };

  return (
    <div
      ref={ref}
      className={`bg-white rounded-xl border border-gray-200 overflow-hidden scroll-mt-16 ${visible ? '' : 'hidden'}`}
    >
      <div
        onClick={toggle}
        onKeyDown={e => { if (isMobile && (e.key === 'Enter' || e.key === ' ')) toggle(); }}
        role={isMobile ? 'button' : undefined}
        tabIndex={isMobile ? 0 : undefined}
        className={`w-full flex items-center justify-between px-3 md:px-6 py-2.5 group ${isMobile ? 'cursor-pointer active:bg-gray-50' : ''}`}
      >
        <div className="flex items-center gap-3">
          {icon && (
            <span className={`material-symbols-outlined transition-colors ${open && isMobile ? 'text-primary' : 'text-gray-600'}`}>
              {icon}
            </span>
          )}
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="flex items-center gap-3">
          {headerActions && <div onClick={e => e.stopPropagation()}>{headerActions}</div>}
          {/* Collapse chevron is a mobile affordance only */}
          <span className={`material-symbols-outlined text-gray-400 transition-transform duration-300 md:hidden ${open ? 'rotate-180' : ''}`}>
            expand_more
          </span>
        </div>
      </div>
      {open && visible && (
        <div className="border-t border-gray-200 px-2.5 md:px-6 py-3 md:py-4">
          {children}
        </div>
      )}
    </div>
  );
};
